// The typed reading of a schedule rule, in words (wireframe 4's "→" line):
// what the rule's columns say, so a reviewer can check it against the quote
// above it. Nothing here computes a date: Mint does.
import { cmDate, daysBetween, todayISO } from './format';
export const RULE_KINDS = [
    {
        value: 'recurring',
        label: 'Recurring'
    },
    {
        value: 'dated',
        label: 'Once'
    },
    {
        value: 'event',
        label: 'After an event'
    }
];
export const RULE_GATES = [
    {
        value: 'base',
        label: 'Base period'
    },
    {
        value: 'option_period',
        label: 'An option period'
    },
    {
        value: 'optional_task',
        label: 'An optional task'
    },
    {
        value: 'none',
        label: 'Whenever the event happens'
    }
];
export const OFFSET_UNITS = [
    {
        value: 'calendar_day',
        label: 'days'
    },
    {
        value: 'business_day',
        label: 'business days'
    },
    {
        value: 'week',
        label: 'weeks'
    },
    {
        value: 'month',
        label: 'months'
    }
];
export const CADENCES = [
    {
        value: 'weekly',
        label: 'Weekly'
    },
    {
        value: 'biweekly',
        label: 'Every two weeks'
    },
    {
        value: 'semimonthly',
        label: 'Twice a month'
    },
    {
        value: 'monthly',
        label: 'Monthly'
    },
    {
        value: 'quarterly',
        label: 'Quarterly'
    },
    {
        value: 'semiannually',
        label: 'Twice a year'
    },
    {
        value: 'annually',
        label: 'Yearly'
    },
    {
        value: 'daily',
        label: 'Daily'
    }
];
/** Events a reviewer logs (never the contract's own native ones). */ export const EVENT_KINDS = [
    {
        value: 'receipt_of_order',
        label: 'Receipt of order (ARO)'
    },
    {
        value: 'exercise_start',
        label: 'Each exercise starts'
    },
    {
        value: 'option_period_start',
        label: 'Each option period starts'
    },
    {
        value: 'award',
        label: 'The award'
    },
    {
        value: 'modification_effective',
        label: 'A modification takes effect'
    },
    {
        value: 'on_request',
        label: 'The government asks for it'
    }
];
/** Read from the contract's own rows (or a government ask): never logged by hand here. */ const NOT_LOGGED_HERE = new Set([
    'award',
    'exercise_start',
    'option_period_start',
    'modification_effective',
    'on_request',
    // SAD-854: the rule's own period start or end, read from its window.
    'pop_start',
    'pop_end',
    'period_start',
    'period_end'
]);
/**
 * SAD-854: an event that is the rule's own period bound ("75 days prior to
 * the end of the PoP") reads as that period's: an optional task's exercise,
 * never the base period. null for any other kind.
 */ function periodBoundWord(kind, gate) {
    const end = kind === 'pop_end' || kind === 'period_end';
    if (!end && kind !== 'pop_start' && kind !== 'period_start') return null;
    const which = end ? 'end' : 'start';
    switch(gate){
        case 'optional_task':
            return `each exercise's PoP ${which}`;
        case 'option_period':
            return `the option period's ${which}`;
        case 'base':
            return `the base period's ${which}`;
        default:
            return `the PoP ${which}`;
    }
}
/** deliverable_accepted_<duty id>: the event kind a deliverable's act logs. */ export function triggerKind(act, dutyId) {
    return `deliverable_${act}_${dutyId}`;
}
/** The act and anchor deliverable of a trigger kind; null for any other kind. */ export function parseTrigger(kind) {
    const m = /^deliverable_(submitted|accepted)_(.+)$/.exec(kind);
    return m ? {
        act: m[1],
        dutyId: m[2]
    } : null;
}
/**
 * Which act a rule's words anchor on: "approval" or "acceptance" is the
 * deliverable's acceptance; anything else ("delivery", "submission") its
 * submission.
 */ export function triggerActFor(ruleText) {
    return /\b(approv|accept)/i.test(ruleText) ? 'accepted' : 'submitted';
}
/**
 * SAD-861: "due alongside" another deliverable. The rule follows that
 * deliverable's dates (each one makes one here, ± days); pangaea reads it as
 * deliverable_due_<duty id>. It is no event anyone logs or waits on.
 */ export function alongsideKind(dutyId) {
    return `deliverable_due_${dutyId}`;
}
/** The deliverable an alongside kind follows; null for any other kind. */ export function parseAlongside(kind) {
    const m = /^deliverable_due_(.+)$/.exec(kind ?? '');
    return m ? m[1] : null;
}
/** "Acceptance of the Program Management Plan". */ export function triggerLabel(act, title) {
    return `${act === 'accepted' ? 'Acceptance' : 'Submission'} of ${title || 'another deliverable'}`;
}
/**
 * SAD-856: the words of the rules anchored on an event kind, so its name reads
 * as the document prints it ("SSP accepted"), never a recased slug.
 */ export function eventTexts(kind, rules) {
    return rules.filter((r)=>r.eventKind === kind).map((r)=>`${r.ruleText ?? ''} ${r.sourceLocator ?? ''}`).join(' ');
}
/**
 * How an event kind reads among the program's deliverables: a trigger names
 * its deliverable, an event a deliverable dates takes that deliverable's title,
 * any other event the words its rules print.
 */ export function eventLabelIn(kind, duties, rules = [], glossary) {
    const t = parseTrigger(kind);
    if (t) return triggerLabel(t.act, duties.find((d)=>d.id === t.dutyId)?.title);
    const along = parseAlongside(kind);
    if (along) return `Dates of ${duties.find((d)=>d.id === along)?.title || 'another deliverable'}`;
    return eventLabel(kind, duties.find((d)=>d.eventKind === kind)?.title, eventTexts(kind, rules), glossary);
}
/**
 * The events a person can log: every kind a rule waits on or a deliverable
 * dates (S6), in label order. Each reads as the deliverable that dates it
 * ("Technical Kickoff Meeting") when there is one.
 */ export function loggableEvents(rules, duties, glossary) {
    const named = new Map();
    for (const d of duties)if (d.eventKind) named.set(d.eventKind, d.title);
    const kinds = new Set(named.keys());
    for (const r of rules)if (r.eventKind && !NOT_LOGGED_HERE.has(r.eventKind) && !parseTrigger(r.eventKind) && !parseAlongside(r.eventKind)) kinds.add(r.eventKind);
    return [
        ...kinds
    ].map((kind)=>({
            kind,
            label: eventLabel(kind, named.get(kind), eventTexts(kind, rules), glossary)
        })).sort((a, b)=>a.label.localeCompare(b.label));
}
/**
 * The events deliverables are waiting on (their `waiting_on_event` check),
 * from ListDuties: each such deliverable's event rules name the kinds. As on
 * pangaea, receipt of order is the reviewer's to date, not an event to log.
 */ export function waitingEvents(rules, duties, checkCodes, glossary) {
    const byKind = new Map();
    for (const r of rules){
        const k = r.eventKind;
        // A trigger (SAD-835) is met by its deliverable's own submission or
        // acceptance, never logged here.
        if (!k || NOT_LOGGED_HERE.has(k) || k === 'receipt_of_order' || parseTrigger(k) || parseAlongside(k)) continue;
        if (!checkCodes[r.dutyId]?.codes.includes('waiting_on_event')) continue;
        byKind.set(k, (byKind.get(k) ?? new Set()).add(r.dutyId));
    }
    const named = new Map(duties.filter((d)=>d.eventKind).map((d)=>[
            d.eventKind,
            d.title
        ]));
    return [
        ...byKind
    ].map(([kind, ids])=>({
            kind,
            label: eventLabel(kind, named.get(kind), eventTexts(kind, rules), glossary),
            dutyIds: [
                ...ids
            ]
        })).sort((a, b)=>a.label.localeCompare(b.label));
}
/**
 * SAD-834: the events whose own date has passed and is still unlogged. A
 * milestone (SAD-869) is never late: its date reads "scheduled", and one
 * past it is overdue to be marked held, never submitted, so Do Next names it
 * apart from the late deliverables. A held event is "held", so it never
 * appears here (the late-held ruling).
 */ export function overdueEvents(rows, today = todayISO()) {
    const byKind = new Map();
    for (const r of rows){
        const e = r.event;
        const state = r.status?.state;
        const past = state === 'scheduled' && !!r.dueOn && r.dueOn < today;
        if (!e || e.projected || state !== 'late' && !past) continue;
        const cur = byKind.get(e.kind);
        const daysLate = past && r.dueOn ? daysBetween(r.dueOn, today) ?? 0 : r.status?.daysLate ?? 0;
        if (!cur || daysLate > cur.daysLate) byKind.set(e.kind, {
            kind: e.kind,
            label: eventLabel(e.kind, e.name),
            daysLate
        });
    }
    return [
        ...byKind.values()
    ].sort((a, b)=>b.daysLate - a.daysLate);
}
/** How many of the rows are late dates of an event (see overdueEvents). */ export function lateEventRows(rows) {
    return rows.filter((r)=>r.event && !r.event.projected && r.status?.state === 'late').length;
}
/**
 * An as-needed condition (pangaea's contractmodel.AsNeededPrefix): something
 * that may happen any number of times or never, so a rule on it is due after
 * each occurrence and is no milestone anyone waits on.
 */ export const AS_NEEDED_PREFIX = 'as_needed_';
export function isAsNeeded(kind) {
    return !!kind && kind.startsWith(AS_NEEDED_PREFIX);
}
/**
 * An event kind's words as the award prints them: a slug word the rule's
 * text prints in capitals ("AGEMO’s", "the SSP") reads in capitals again
 * ("agemo comments …" → "AGEMO comments …"). A word the text does not print
 * takes the program glossary's printing when it has one (SAD-857: three
 * letters or more, so "it" never becomes "IT"), else stays as the slug has it.
 */ export function eventWords(kind, ruleText = '', glossary) {
    const words = (isAsNeeded(kind) ? kind.slice(AS_NEEDED_PREFIX.length) : kind).split('_').filter(Boolean);
    const printed = new Map();
    for (const m of ruleText.matchAll(/[A-Za-z0-9]+/g)){
        const w = m[0];
        if (w.length >= 2 && w === w.toUpperCase() && /[A-Z]/.test(w)) printed.set(w.toLowerCase(), w);
    }
    return words.map((w)=>printed.get(w) ?? (w.length >= 3 ? glossary?.(w) : undefined) ?? w).join(' ');
}
/**
 * "Kickoff meeting" for kickoff_meeting; `title` (the dating deliverable)
 * wins. Otherwise the kind in the words `ruleText` prints, acronyms as printed
 * (SAD-856): every caller passes the words of the rules anchored on the kind
 * (eventTexts), so an acronym is never recased into "Ssp".
 */ export function eventLabel(kind, title, ruleText, glossary) {
    if (title) return title;
    const known = EVENT_KINDS.find((k)=>k.value === kind)?.label;
    if (known) return known;
    const words = eventWords(kind, ruleText, glossary);
    if (!words) return '';
    // Capitalise a slug word, never a printing the text or glossary supplied
    // ("eBay" stays "eBay").
    const slugFirst = (isAsNeeded(kind) ? kind.slice(AS_NEEDED_PREFIX.length) : kind).split('_').find(Boolean);
    return words.startsWith(slugFirst ?? '') ? words[0].toUpperCase() + words.slice(1) : words;
}
const CADENCE_WORD = {
    daily: 'daily',
    weekly: 'weekly',
    biweekly: 'every two weeks',
    semimonthly: 'twice a month',
    monthly: 'monthly',
    quarterly: 'quarterly',
    semiannually: 'twice a year',
    annually: 'yearly'
};
const UNIT_WORD = {
    calendar_day: [
        'day',
        'days'
    ],
    business_day: [
        'business day',
        'business days'
    ],
    week: [
        'week',
        'weeks'
    ],
    month: [
        'month',
        'months'
    ]
};
const EVENT_WORD = {
    receipt_of_order: 'receipt of order',
    exercise_start: 'each exercise starts',
    option_period_start: 'each option period starts',
    award: 'the award',
    modification_effective: 'a modification takes effect',
    on_request: 'each government request'
};
function anchorWord(gate) {
    switch(gate){
        case 'optional_task':
            return "each exercise's PoP start";
        case 'option_period':
            return "the option period's start";
        case 'base':
            return "the base period's start";
        case 'sow':
            return "the SOW's start";
        default:
            return 'the start';
    }
}
function endWord(gate) {
    switch(gate){
        case 'optional_task':
            return "until that exercise's PoP end";
        case 'option_period':
            return 'until the option period ends';
        case 'base':
            return 'until the base period ends';
        case 'sow':
            return 'while the SOW is in force';
        default:
            return '';
    }
}
function offsetPhrase(r, anchor) {
    if (!r.hasOffset) return anchor;
    const n = r.offsetN;
    const [one, many] = UNIT_WORD[r.offsetUnit] ?? [
        'day',
        'days'
    ];
    if (n === 0) return `on ${anchor}`;
    const abs = Math.abs(n);
    return `${abs} ${abs === 1 ? one : many} ${n < 0 ? 'before' : 'after'} ${anchor}`;
}
export function cadencePhrase(r) {
    const word = CADENCE_WORD[r.cadence] ?? r.cadence;
    if (r.cadenceInterval > 1) {
        if (r.cadence === 'weekly') return `every ${r.cadenceInterval} weeks`;
        if (r.cadence === 'monthly') return `every ${r.cadenceInterval} months`;
        if (r.cadence === 'annually') return `every ${r.cadenceInterval} years`;
    }
    return word;
}
/** "1st", "2nd", "23rd". */ export function ordinalWord(n) {
    const tens = n % 100;
    if (tens >= 11 && tens <= 13) return `${n}th`;
    return `${n}${({
        1: 'st',
        2: 'nd',
        3: 'rd'
    })[n % 10] ?? 'th'}`;
}
const WEEKDAY_ORDINAL = {
    1: 'first',
    2: 'second',
    3: 'third',
    4: 'fourth',
    5: 'fifth',
    [-1]: 'last'
};
/** The calendar period a day rule counts within: "month", "quarter", … */ export function periodNoun(cadence) {
    switch(cadence){
        case 'quarterly':
            return 'quarter';
        case 'semiannually':
            return 'half-year';
        case 'annually':
            return 'year';
        case 'weekly':
        case 'biweekly':
            return 'week';
        default:
            return 'month';
    }
}
function capitalized(weekday) {
    return `${weekday[0].toUpperCase()}${weekday.slice(1)}`;
}
/**
 * ", on the 5th business day of the month", ", on the last Thursday of the
 * quarter", ", on Friday". The words a reviewer checks against the contract.
 */ export function dayPhrase(r) {
    const of = periodNoun(r.cadence);
    if (r.dayWeekday && r.dayOrdinals.length > 0) {
        const ords = r.dayOrdinals.map((d)=>WEEKDAY_ORDINAL[d] ?? ordinalWord(d)).join(' and ');
        return `, on the ${ords} ${capitalized(r.dayWeekday)} of the ${of}`;
    }
    if (r.dayWeekday) return `, on ${capitalized(r.dayWeekday)}`;
    if (r.dayOrdinals.length > 0) {
        const business = r.dayBasis === 'business_day';
        const days = r.dayOrdinals.map((d)=>d === -1 ? `the last ${business ? 'business day' : 'day'}` : `the ${ordinalWord(d)}${business ? ' business day' : ''}`).join(' and ');
        return `, on ${days} of the ${of}`;
    }
    return '';
}
/**
 * "7 days after each exercise's PoP start, then monthly, until that
 * exercise's PoP end". A rule with no first date reads as such, so the reading
 * itself shows why no dates exist.
 */ export function describeRule(r, titleOf) {
    const along = parseAlongside(r.eventKind);
    if (along) {
        const title = titleOf?.(along) || 'another deliverable';
        const n = r.hasOffset ? r.offsetN : 0;
        const [one, many] = UNIT_WORD[r.offsetUnit] ?? [
            'day',
            'days'
        ];
        const abs = Math.abs(n);
        return n === 0 ? `Due alongside ${title}: one on each of its due dates` : `Due ${abs} ${abs === 1 ? one : many} ${n < 0 ? 'before' : 'after'} each ${title} date`;
    }
    const trigger = r.eventKind ? parseTrigger(r.eventKind) : null;
    const anchor = trigger ? `${trigger.act === 'accepted' ? 'acceptance' : 'submission'} of ${titleOf?.(trigger.dutyId) || 'another deliverable'}` : r.eventKind ? periodBoundWord(r.eventKind, r.gate) ?? EVENT_WORD[r.eventKind] ?? eventWords(r.eventKind, r.ruleText) : anchorWord(r.gate);
    let text;
    if (r.kind === 'dated') {
        text = r.dueOn ? `Once, on ${cmDate(r.dueOn)}` : `Once, ${offsetPhrase(r, anchor)}`;
        if (!r.dueOn && !r.hasOffset) text = 'Once, date not stated';
    } else if (r.kind === 'event' && isAsNeeded(r.eventKind)) {
        const occurrence = eventWords(r.eventKind, r.ruleText);
        const [one, many] = UNIT_WORD[r.offsetUnit] ?? [
            'day',
            'days'
        ];
        const n = Math.abs(r.offsetN);
        text = r.hasOffset ? `As needed: within ${n} ${n === 1 ? one : many} of each ${occurrence}` : `As needed: on each ${occurrence}`;
    } else if (r.kind === 'event') {
        text = `${offsetPhrase(r, anchor)}`;
        text = text[0].toUpperCase() + text.slice(1);
    } else {
        const first = r.dueOn ? `From ${cmDate(r.dueOn)}` : r.hasOffset ? offsetPhrase(r, anchor) : r.dayWeekday || r.dayOrdinals.length > 0 ? `From ${anchor}` : 'No first date stated';
        text = `${first[0].toUpperCase()}${first.slice(1)}, then ${cadencePhrase(r)}${dayPhrase(r)}`;
        const end = r.eventKind ? '' : endWord(r.gate);
        if (end) text += `, ${end}`;
    }
    if (r.effectiveFrom || r.effectiveTo) {
        text += ` (in effect ${r.effectiveFrom ? `from ${cmDate(r.effectiveFrom)}` : ''}${r.effectiveFrom && r.effectiveTo ? ' ' : ''}${r.effectiveTo ? `until ${cmDate(r.effectiveTo)}` : ''})`;
    }
    return text;
}
/** A recurring or dated rule with no stated date, no offset and no day rule has no first date. */ export function ruleLacksStart(r) {
    if (r.kind === 'event') return false;
    if (r.dueOn || r.hasOffset) return false;
    if (r.kind === 'recurring' && (r.dayWeekday || r.dayOrdinals.length > 0)) return false;
    return true;
}
/**
 * SAD-854: the optional task a deliverable belongs to, when every one of its
 * award rules is pinned to that one task; null when any rule applies in a
 * period, or the rules name different tasks.
 */ export function dutyTaskNumber(rules, placeOf) {
    let task = null;
    for (const r of rules){
        if (r.assignmentId) continue;
        const p = placeOf(r);
        if (p.kind !== 'ot' || p.n === undefined || task !== null && task !== p.n) return null;
        task = p.n;
    }
    return task;
}
/** Everything that makes two rules the same rule, wherever each applies. */ function ruleSignature(r) {
    return JSON.stringify([
        r.ruleText,
        r.kind,
        r.dueOn,
        r.hasOffset,
        r.offsetN,
        r.offsetUnit,
        r.cadence,
        r.cadenceInterval,
        r.dayOrdinals,
        r.dayWeekday,
        r.dayBasis,
        r.eventKind,
        r.effectiveFrom,
        r.effectiveTo,
        r.assignmentId ?? ''
    ]);
}
/**
 * SAD-851: a schedule row printed once per period ("0101AA … 4101AA") states
 * the same rule in each. Identical rules read once, with the places they
 * cover; each stays its own rule, so every period's dates still mint.
 */ export function groupRules(rules, placeOf) {
    const groups = new Map();
    for (const r of rules){
        const key = ruleSignature(r);
        const g = groups.get(key) ?? {
            rules: [],
            places: []
        };
        g.rules.push(r);
        g.places.push(placeOf(r));
        groups.set(key, g);
    }
    return [
        ...groups.values()
    ].map((g)=>{
        const order = g.places.map((_p, i)=>i).sort((a, b)=>g.places[a].order - g.places[b].order);
        const places = order.map((i)=>g.places[i]);
        return {
            rules: order.map((i)=>g.rules[i]),
            places,
            label: placesLabel(places)
        };
    });
}
/** "Base, OP1–OP4": places in order, a run of consecutive numbers joined. */ export function placesLabel(places) {
    const out = [];
    for(let i = 0; i < places.length;){
        const p = places[i];
        let j = i;
        while(p.n !== undefined && j + 1 < places.length && places[j + 1].kind === p.kind && places[j + 1].n === (places[j].n ?? 0) + 1)j++;
        out.push(j - i >= 2 ? `${p.label}–${places[j].label}` : places.slice(i, j + 1).map((x)=>x.label).join(', '));
        i = j + 1;
    }
    return out.join(', ');
}
/** The mint receipt: "12 dates added", "10 retired, 3 added", "No dates changed". */ export function mintReceipt(m) {
    if (!m) return 'Saved';
    const parts = [];
    const n = (x, w)=>`${x} ${x === 1 ? 'date' : 'dates'} ${w}`;
    if (m.retired) parts.push(n(m.retired, 'retired'));
    if (m.inserted) parts.push(n(m.inserted, 'added'));
    if (m.updated) parts.push(n(m.updated, 'moved'));
    if (m.revived) parts.push(n(m.revived, 'restored'));
    return parts.length ? parts.join(', ') : 'No dates changed';
}
